import React, { useState, useEffect, useRef } from "react"
import { useLocation, useHistory } from "react-router-dom"
import { db, logUserEvent } from "../common/firebase.utils"
import TemplatesView from "./TemplatesView"
import {
  ORDER_BY,
  ORDER,
  PUBLIC_LIMIT_COUNT_SITUATION,
  PUBLIC_LIMIT_COUNT_ALL,
  LENGTH
} from "../common/constants"
import QueryString from "query-string-for-all"
import { useUserInfo } from "../hooks/useUserInfo"
import { isLimited } from "../common/utils"
import {
  showLoginPopup,
  showPaymentPopup,
  showUnlockPopup
} from "../store/slices/layerSlice"
import { useDispatch } from "react-redux"
import algoliasearch from "algoliasearch/lite"
import FilterPopup from "../common/FilterPopup"
import TemplateEditPopup from "./TemplateEditPopup"

const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID,
  process.env.REACT_APP_ALGOLIA_API_KEY
)

function Templates() {
  const [isLoading, setIsLoading] = useState(false)
  const [gettingMore, setGettingMore] = useState(false)
  const [noMoreToLoad, setNoMoreToLoad] = useState(false)
  const [templates, setTemplates] = useState([])
  const [showFilterPopup, setShowFilterPopup] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [hasEnoughTemplatesForRandom, setHasEnoughTemplatesForRandom] =
    useState(true)

  const [options, setOptions] = useState({})

  const lastVisible = useRef(null)
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const {
    isLoading: isLoadingLogin,
    loggedIn,
    unlimitedUntil,
    membership
  } = useUserInfo()

  const isLimitedUser = membership === "default" && isLimited(unlimitedUntil)
  const BLUR_COUNT = 6
  const LIMITED_COUNT = options.situation
    ? PUBLIC_LIMIT_COUNT_SITUATION
    : PUBLIC_LIMIT_COUNT_ALL
  const LOAD_COUNT = isLimitedUser ? LIMITED_COUNT + BLUR_COUNT : 9
  const DEFAULT_ORDER_BY = ORDER_BY.CREATED_AT
  const DEFAULT_ORDER = ORDER.DESC

  // 랜덤 정렬 비활성화 조건: 제한된 사용자이거나 충분한 템플릿이 없을 때
  const shouldDisableRandom = isLimitedUser || !hasEnoughTemplatesForRandom

  // 랜덤 비활성화 이유
  const randomDisabledReason = isLimitedUser
    ? "템플릿 등록 또는 멤버십 가입 후 이용 가능"
    : !hasEnoughTemplatesForRandom
    ? "템플릿이 충분하지 않습니다"
    : null

  // 현재 랜덤 모드인데 랜덤이 비활성화되어야 하는 경우 기본 정렬로 전환
  useEffect(() => {
    if (shouldDisableRandom && options.orderby === ORDER_BY.RANDOM) {
      updateSearchParams({
        ...options,
        orderby: DEFAULT_ORDER_BY,
        order: DEFAULT_ORDER
      })
    }
  }, [shouldDisableRandom, options.orderby]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [])

  useEffect(() => {
    const _options = QueryString.parse(location.search)
    setOptions(_options)
    // 옵션이 변경되면 랜덤 활성화 상태 초기화 (새로운 필터 조건이므로)
    setHasEnoughTemplatesForRandom(true)
  }, [location.search])

  useEffect(() => {
    loadTemplates()
  }, [options]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoading) {
      setIsLoading(false)
    }
  }, [templates]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadTemplates = async isGettingMore => {
    const {
      tag,
      language,
      orderby = DEFAULT_ORDER_BY,
      order = DEFAULT_ORDER,
      situation,
      receiver,
      length
    } = options

    if (tag) {
      loadTemplatesWithTag(tag)
      return
    }

    // 랜덤 정렬인 경우 별도 처리
    if (orderby === ORDER_BY.RANDOM) {
      loadRandomTemplates()
      return
    }

    let query = db.collection("templates").where("deletedat", "==", null)
    if (language) {
      if (language === "etc") {
        query = query.orderBy("language")
        query = query.where("language", "not-in", ["kor", "eng"])
      } else {
        query = query.where("language", "==", language)
      }
    }
    if (situation) {
      query = query.where("situation", "==", situation)
    }
    if (receiver) {
      query = query.where("receiver", "==", receiver)
    }
    if (length) {
      query = query
        .where("length", length === LENGTH.LONG ? ">=" : "<", 300)
        .orderBy("length", length === LENGTH.LONG ? "desc" : "asc")
    }
    query = query.orderBy(orderby, order)
    if (isGettingMore) {
      query = query.startAfter(lastVisible.current)
    } else {
      setIsLoading(true)
      logSearchEvent()
    }

    query
      .limit(LOAD_COUNT)
      .get()
      .then(async snapshot => {
        let _templates = isGettingMore ? [...templates] : []
        let data = null
        snapshot.docs.forEach(doc => {
          data = doc.data()
          _templates.push({
            ...data,
            id: doc.id
          })
          lastVisible.current = doc
        })
        setNoMoreToLoad(snapshot.size < LOAD_COUNT)

        if (isGettingMore) {
          setGettingMore(false)
        }
        setTemplates(_templates)

        // 처음 로드할 때만 랜덤 활성화 상태 체크 (결과 기반)
        if (!isGettingMore) {
          // 현재 결과가 LOAD_COUNT보다 적고, 더 로드할 것이 없으면 랜덤 비활성화
          if (_templates.length < LOAD_COUNT && snapshot.size < LOAD_COUNT) {
            setHasEnoughTemplatesForRandom(false)
          } else {
            setHasEnoughTemplatesForRandom(true)
          }
        }
      })
  }

  const buildBaseQuery = () => {
    const { language, situation, receiver, length } = options
    let query = db.collection("templates").where("deletedat", "==", null)

    if (language) {
      if (language === "etc") {
        query = query.where("language", "not-in", ["kor", "eng"])
      } else {
        query = query.where("language", "==", language)
      }
    }
    if (situation) {
      query = query.where("situation", "==", situation)
    }
    if (receiver) {
      query = query.where("receiver", "==", receiver)
    }
    if (length) {
      query = query.where("length", length === LENGTH.LONG ? ">=" : "<", 300)
    }

    return query
  }

  const loadRandomTemplates = async () => {
    setIsLoading(true)
    logSearchEvent()

    try {
      // 더 진정한 랜덤을 위해 여러 다른 접근 방식을 조합
      const randomTemplates = await fetchRandomTemplatesWithVariety()

      setTemplates(randomTemplates)
      setNoMoreToLoad(true)

      // 랜덤 로드 후 충분한 템플릿이 있는지 체크
      if (randomTemplates.length < LOAD_COUNT) {
        setHasEnoughTemplatesForRandom(false)
      }
    } catch (error) {
      console.error("Error loading random templates:", error)
      setTemplates([])
      setHasEnoughTemplatesForRandom(false)
    }
  }

  const fetchRandomTemplatesWithVariety = async () => {
    const templates = []
    const usedIds = new Set()

    // 여러 다른 전략으로 랜덤 템플릿 수집
    const strategies = [
      () => fetchRandomByTimeRange("recent"), // 최근 템플릿
      () => fetchRandomByTimeRange("old"), // 오래된 템플릿
      () => fetchRandomByTimeRange("middle"), // 중간 템플릿
      () => fetchRandomByPopularity("high"), // 인기 템플릿
      () => fetchRandomByPopularity("low") // 저조한 템플릿
    ]

    for (const strategy of strategies) {
      if (templates.length >= LOAD_COUNT) break

      try {
        const strategyTemplates = await strategy()

        // 중복 제거하며 추가
        for (const template of strategyTemplates) {
          if (!usedIds.has(template.id) && templates.length < LOAD_COUNT) {
            templates.push(template)
            usedIds.add(template.id)
          }
        }
      } catch (error) {
        console.warn("Strategy failed:", error)
      }
    }

    // 부족한 경우 기본 랜덤으로 채우기
    if (templates.length < LOAD_COUNT) {
      const additionalTemplates = await fetchBasicRandom(
        LOAD_COUNT - templates.length,
        usedIds
      )
      templates.push(...additionalTemplates)
    }

    // 최종 섞기
    return shuffleArray(templates.slice(0, LOAD_COUNT))
  }

  const fetchRandomByTimeRange = async timeRange => {
    let query = buildBaseQuery()

    // 시간대별로 다른 정렬
    if (timeRange === "recent") {
      query = query.orderBy("createdat", "desc")
    } else if (timeRange === "old") {
      query = query.orderBy("createdat", "asc")
    } else {
      // 중간: 랜덤하게 오름차순/내림차순
      query = query.orderBy("createdat", Math.random() > 0.5 ? "desc" : "asc")
    }

    // 랜덤한 위치에서 시작하기 위해 많이 가져온 후 랜덤 선택
    const snapshot = await query.limit(50).get()

    if (snapshot.empty) return []

    const templates = []
    snapshot.docs.forEach(doc => {
      templates.push({
        ...doc.data(),
        id: doc.id
      })
    })

    return shuffleArray(templates).slice(0, 3)
  }

  const fetchRandomByPopularity = async popularityLevel => {
    let query = buildBaseQuery()

    // 인기도별 정렬
    if (popularityLevel === "high") {
      query = query.orderBy("pasted", "desc").orderBy("createdat", "desc")
    } else {
      query = query
        .orderBy("pasted", "asc")
        .orderBy("createdat", Math.random() > 0.5 ? "desc" : "asc")
    }

    const snapshot = await query.limit(30).get()

    if (snapshot.empty) return []

    const templates = []
    snapshot.docs.forEach(doc => {
      templates.push({
        ...doc.data(),
        id: doc.id
      })
    })

    return shuffleArray(templates).slice(0, 2)
  }

  const fetchBasicRandom = async (count, usedIds) => {
    let query = buildBaseQuery()
    query = query.orderBy("createdat", Math.random() > 0.5 ? "desc" : "asc")

    const snapshot = await query.limit(count * 3).get()

    if (snapshot.empty) return []

    const templates = []
    snapshot.docs.forEach(doc => {
      if (!usedIds.has(doc.id)) {
        templates.push({
          ...doc.data(),
          id: doc.id
        })
      }
    })

    return shuffleArray(templates).slice(0, count)
  }

  // Fisher-Yates 셔플 알고리즘
  const shuffleArray = array => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }

  const logSearchEvent = () => {
    const params = {}
    const { situation, language, receiver } = options
    if (situation) {
      params.search_term = situation
    }
    if (language) {
      params.filter_language = language
    }
    if (receiver) {
      params.filter_receiver = receiver
    }
    logUserEvent("search", params)
  }

  const loadTemplatesWithTag = async tag => {
    setIsLoading(true)
    setNoMoreToLoad(true)
    const snapshot = await db.collection("tags").where("title", "==", tag).get()
    const templateCount = snapshot.size
    if (!templateCount) {
      setTemplates([])
    } else {
      const _templates = []
      snapshot.docs.forEach((tagDoc, tagIndex) => {
        let query = db.collection("templates").doc(tagDoc.data().templateid)
        query
          .get()
          .then(template => {
            if (template.exists) {
              let data = template.data()
              if (!data.deletedat) {
                if (
                  !options.language ||
                  options.language === data.language ||
                  (options.language === "etc" &&
                    !["kor", "eng"].includes(data.language))
                ) {
                  _templates.push({
                    ...data,
                    id: template.id
                  })
                }
              }
            }
            if (tagIndex === templateCount - 1) {
              setTemplates(_templates)
            }
          })
          .catch(function (error) {
            console.log("Error getting document:", error)
          })
      })
    }
  }

  const loadMore = () => {
    setGettingMore(true)
    loadTemplates(true)
  }

  const showAll = () => {
    updateSearchParams({})
  }

  const onFilterClick = () => {
    setShowFilterPopup(true)
    logUserEvent("click_filter")
  }

  const onApplyFilter = filter => {
    if (filter) {
      updateSearchParams({
        ...options,
        language: filter.language,
        receiver: filter.receiver,
        length: filter.length
      })
    }
    setShowFilterPopup(false)
  }

  const setLanguage = lang => {
    updateSearchParams({
      ...options,
      language: lang === options.language ? undefined : lang
    })
  }

  const setOrderBy = (field, isDisabled) => {
    if (field === ORDER_BY.RANDOM) {
      if (isDisabled) {
        if (hasEnoughTemplatesForRandom && isLimitedUser) {
          dispatch(showUnlockPopup())
        }
        return
      }

      // 랜덤 정렬의 경우 매번 새로운 결과를 보여주기 위해 즉시 로드
      logUserEvent("click_orderby", {
        value: field
      })

      // 이미 랜덤 모드인 경우에도 새로운 랜덤 결과 로드
      if (options.orderby === ORDER_BY.RANDOM) {
        // 페이지 상단으로 스크롤
        window.scrollTo({ top: 0, behavior: "smooth" })
        // 바로 새로운 랜덤 템플릿 로드
        loadRandomTemplates()
      } else {
        // 처음 랜덤 모드로 전환하는 경우
        updateSearchParams({
          ...options,
          orderby: ORDER_BY.RANDOM,
          order: ORDER.DESC // 랜덤에서는 order가 의미 없지만 일관성을 위해 설정
        })
      }
      return
    }

    let _orderby = options.orderby || DEFAULT_ORDER_BY
    let _order = options.order || DEFAULT_ORDER
    if (field === ORDER_BY.LAST_COPIED_AT && field === _orderby) {
      // chainging to desc order is not supported for this order.
      return
    }
    if (field === _orderby) {
      _order = _order === ORDER.ASC ? ORDER.DESC : ORDER.ASC
    } else {
      _orderby = field
      _order = ORDER.DESC
    }
    updateSearchParams({
      ...options,
      orderby: _orderby,
      order: _order
    })
    logUserEvent("click_orderby", {
      value: field
    })
  }

  const setSituation = situation => {
    if (situation) {
      updateSearchParams({ ...options, tag: undefined, situation })
    } else {
      showAll()
    }
  }

  const setTag = tag => {
    updateSearchParams({ language: options.language, tag })
  }

  const updateSearchParams = searchParams => {
    history.push("?" + QueryString.stringify(searchParams))
  }

  const onClickRequestTemplate = () => {
    logUserEvent("click_request_on_empty_view")
    history.push(
      loggedIn ? "/community/templates/write" : "/community/templates"
    )
  }

  const onClickWriteToUnlimitButton = () => {
    logUserEvent("click_write_to_unlock")
    if (loggedIn) {
      history.push("/write")
    } else {
      dispatch(
        showLoginPopup({
          message: "환영합니다!\n간편하게 로그인하고 템플릿을 등록하세요.",
          redirect: "/write"
        })
      )
    }
  }

  const onClickJoinMembership = () => {
    logUserEvent("click_join_on_templates_top")
    if (loggedIn) {
      dispatch(showPaymentPopup())
    } else {
      dispatch(
        showLoginPopup({
          message: "환영합니다!\n간편하게 로그인하고 멤버십을 가입하세요."
        })
      )
    }
  }

  const onClickEditTemplate = template => {
    setEditingTemplate(template)
  }

  const shuffleRandomTemplates = () => {
    // 랜덤 모드에서 다시 섞기 버튼을 눌렀을 때 호출
    if (options.orderby === ORDER_BY.RANDOM) {
      // 페이지 상단으로 스크롤
      window.scrollTo({ top: 0, behavior: "smooth" })
      loadRandomTemplates()
    }
  }

  return (
    <>
      <TemplatesView
        options={options}
        searchClient={searchClient}
        onSelectSituation={setSituation}
        onSelectLanguage={setLanguage}
        onSelectOrderBy={setOrderBy}
        onFilterClick={onFilterClick}
        isLoading={isLoading}
        gettingMore={gettingMore}
        noMoreToLoad={isLimitedUser || noMoreToLoad}
        isLoadingLogin={isLoadingLogin}
        isLimited={isLimitedUser}
        membership={membership}
        blurFrom={LIMITED_COUNT}
        templates={templates}
        loadMore={loadMore}
        shuffleRandomTemplates={shuffleRandomTemplates}
        shouldDisableRandom={shouldDisableRandom}
        randomDisabledReason={randomDisabledReason}
        onSelectTag={setTag}
        onApplyFilter={onApplyFilter}
        onClickRequestTemplate={onClickRequestTemplate}
        onClickWriteToUnlimitButton={onClickWriteToUnlimitButton}
        onClickJoinMembership={onClickJoinMembership}
        onClickEditTemplate={onClickEditTemplate}
      />
      {showFilterPopup && (
        <FilterPopup
          currentFilters={{
            language: options.language,
            receiver: options.receiver,
            length: options.length
          }}
          membership={membership}
          onApply={onApplyFilter}
        />
      )}
      {editingTemplate && (
        <TemplateEditPopup
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
        />
      )}
    </>
  )
}
export default Templates
